// ---------------------------------------------------------------------------
// why-now-agent-beat - the script for the fixture's third beat, "Bring your
// agents in.". Pure data and maths: one timeline (ms from the beat's start)
// that BOTH renderers read - the DOM chat overlay for its stages, the graph
// canvas for the agent tile, the read trails and the write comets - so the
// tool label in the chat and the light on the graph can never drift apart.
//
// The story: the viewer tags Claude Code in the thread, the mention chip lifts
// out of the message and docks beside the memory as a tile, the agent READS along
// the existing edges (launch copy, the two happiest clients, the growth owner),
// then WRITES: two new nodes pop in, one is updated, and the new work is linked
// across to the CRM clients whose quotes it used. All the while the WORK shows:
// a tiny mock of the team's homepage sits beside the graph and gains a ratings
// row and the two quotes, one by one, as the reads come back.
// ---------------------------------------------------------------------------

import { clamp01 } from '../ledger-scene/why-now-flight';
import { WHY_NOW_ROOT_ID } from '../ledger-scene/why-now-graph';
import type { Point } from '../ledger-scene/why-now-scene';

/** The agent the viewer tags in the scripted thread. */
export const WHY_NOW_AGENT_NAME = 'Claude Code';

/** Claude's brand clay - the tint every agent-authored mark carries. */
export const AGENT_COLOR = '#D97757';

/**
 * The beat never waits for the graph: it starts as the viewer reaches it, any
 * assembly still in flight is rushed to land (ASSEMBLY_RUSH_MS), and this layer
 * takes the camera over from the assembly's fit loop and re-frames the
 * still-settling graph itself until the chip lifts off.
 */
export const AGENT_REFRAME_MS = [1100, 2200];

export const AGENT_MESSAGE = 'add user ratings to our homepage, surprise me';

const TYPE_START_MS = 600;
const TYPE_MS_PER_CHAR = 30;
/** The composer types "@Claude Code " as one chip, then the message. */
export const AGENT_TYPE_END_MS = TYPE_START_MS + AGENT_MESSAGE.length * TYPE_MS_PER_CHAR;

const SEND_MS = AGENT_TYPE_END_MS + 350;
/** The sent message's @-chip lifts off and flies to where the tile docks. */
const LAUNCH_MS = SEND_MS + 400;
export const CHIP_FLIGHT_MS = 850;
const DOCK_MS = LAUNCH_MS + CHIP_FLIGHT_MS;
const READ_START_MS = DOCK_MS + 650;

export const READ_OUT_MS = 620;
export const READ_BACK_MS = 520;
export const COMET_MS = 720;
export const BORN_POP_MS = 460;
export const BURST_MS = 900;
export const LINK_DRAW_MS = 700;
export const DOCK_WAVE_MS = 1100;
export const AGENT_FADE_MS = 320;

export interface AgentRead {
	id: string;
	at: number;
}

/** Each read: a pulse out along the tree to the node, then the fact back. */
export const AGENT_READS: AgentRead[] = [
	{ id: 'task-launch-copy', at: READ_START_MS },
	{ id: 'doc-pricing-page', at: READ_START_MS + 380 },
	{ id: 'client-meridian', at: READ_START_MS + 1000 },
	{ id: 'client-klarwerk', at: READ_START_MS + 1300 },
	{ id: 'person-maya', at: READ_START_MS + 1950 },
];

const READ_END_MS = READ_START_MS + 1950 + READ_OUT_MS + READ_BACK_MS;
const WRITE_START_MS = READ_END_MS + 350;

export type AgentWriteKind = 'born' | 'updated';

export interface AgentWrite {
	id: string;
	kind: AgentWriteKind;
	/** Comet launch; it lands COMET_MS later. */
	at: number;
}

export const AGENT_WRITES: AgentWrite[] = [
	{ id: 'task-user-ratings', kind: 'born', at: WRITE_START_MS },
	{ id: 'doc-ratings-pr', kind: 'born', at: WRITE_START_MS + 420 },
	{ id: 'task-launch-copy', kind: 'updated', at: WRITE_START_MS + 840 },
];

const LINKS_MS = WRITE_START_MS + 840 + COMET_MS + 250;

export interface AgentLink {
	source: string;
	target: string;
	at: number;
}

/** The "surprise": the new work is tied to the clients whose quotes it used. */
export const AGENT_LINKS: AgentLink[] = [
	{ source: 'task-user-ratings', target: 'client-meridian', at: LINKS_MS },
	{ source: 'task-user-ratings', target: 'client-klarwerk', at: LINKS_MS + 220 },
];

const REPLY_MS = LINKS_MS + 220 + LINK_DRAW_MS + 450;
const REACTION_MS = REPLY_MS + 1100;

export const AGENT_T = {
	typeStart: TYPE_START_MS,
	send: SEND_MS,
	launch: LAUNCH_MS,
	dock: DOCK_MS,
	readStart: READ_START_MS,
	writeStart: WRITE_START_MS,
	links: LINKS_MS,
	reply: REPLY_MS,
	reaction: REACTION_MS,
	/** Past this nothing moves - the canvas may stop redrawing. */
	end: REACTION_MS + 900,
} as const;

/** Small labels the canvas writes next to every node the agent touched. */
export const AGENT_NODE_LABELS: Record<string, { label: string; tag?: string }> = {
	'task-launch-copy': { label: 'Launch copy', tag: 'updated' },
	'doc-pricing-page': { label: 'Pricing page' },
	'client-meridian': { label: 'Meridian' },
	'client-klarwerk': { label: 'Klarwerk' },
	'person-maya': { label: 'Maya' },
	'task-user-ratings': { label: 'User ratings', tag: 'new' },
	'doc-ratings-pr': { label: 'PR #412', tag: 'new' },
};

/**
 * DOM stages of the chat overlay. 0 is "not on stage"; the working label under
 * Claude Code's name follows the graph (read -> think -> write).
 */
export const AGENT_CHAT_STAGES = [
	{ stage: 1, at: 0 }, // panel in, composer typing
	{ stage: 2, at: SEND_MS }, // message sent, chip about to lift
	{ stage: 3, at: DOCK_MS + 250 }, // Claude Code row: reading
	{ stage: 4, at: READ_START_MS + 1000 }, // ...finding customers
	{ stage: 5, at: WRITE_START_MS }, // ...writing back
	{ stage: 6, at: REPLY_MS }, // the reply + Memory updated
	{ stage: 7, at: REACTION_MS }, // a teammate reacts
] as const;
export const AGENT_CHAT_FINAL_STAGE = 7;

/** A pulse from the tile into the page; what it carries appears as it lands. */
export const PAGE_PUSH_MS = 520;

/**
 * Stages of the mock homepage the agent edits. Each change starts just after
 * the read that feeds it has come back to the tile, so the page visibly fills
 * up with what the graph handed over.
 */
export const AGENT_PAGE_STAGES = [
	{ stage: 1, at: DOCK_MS + 250 }, // the window slides in: the page as it was
	{ stage: 2, at: READ_START_MS + 900 }, // the ratings row
	{ stage: 3, at: READ_START_MS + 2300 }, // ...Meridian's quote
	{ stage: 4, at: READ_START_MS + 3000 }, // ...Klarwerk's quote
] as const;
export const AGENT_PAGE_FINAL_STAGE = 4;

/** When the canvas fires a pulse from the tile into the page (one per change). */
export const AGENT_PAGE_PUSHES: number[] = AGENT_PAGE_STAGES.filter((entry) => entry.stage >= 2).map(
	(entry) => entry.at,
);
export const AGENT_PAGE_TETHER_MS = DOCK_MS + 250;

export const AGENT_WORKING_LABELS: Record<number, string> = {
	3: 'Reading Launch copy',
	4: 'Building the ratings strip',
	5: 'Writing back to memory',
};

/** How many characters of AGENT_MESSAGE the composer shows at `elapsed`. */
export const typedChars = (elapsed: number) =>
	Math.round(clamp01((elapsed - TYPE_START_MS) / (AGENT_TYPE_END_MS - TYPE_START_MS)) * AGENT_MESSAGE.length);

/** Root-first chain of node ids from the memory down to `id`. */
export const pathFromRoot = (id: string, parentOf: Map<string, string>): string[] => {
	const chain = [id];
	let current = id;
	while (current !== WHY_NOW_ROOT_ID) {
		const parent = parentOf.get(current);
		if (!parent) break;
		chain.unshift(parent);
		current = parent;
	}
	return chain;
};

/** The point `t` (0..1, by length) of the way along a polyline. */
export const pointAlong = (points: Point[], t: number): Point => {
	if (points.length === 0) return { x: 0, y: 0 };
	if (points.length === 1) return points[0];
	const lengths: number[] = [];
	let total = 0;
	for (let i = 1; i < points.length; i += 1) {
		const length = Math.hypot(points[i].x - points[i - 1].x, points[i].y - points[i - 1].y);
		lengths.push(length);
		total += length;
	}
	if (total === 0) return points[0];
	let remaining = clamp01(t) * total;
	for (let i = 0; i < lengths.length; i += 1) {
		if (remaining <= lengths[i] || i === lengths.length - 1) {
			const local = lengths[i] === 0 ? 0 : Math.min(1, remaining / lengths[i]);
			return {
				x: points[i].x + (points[i + 1].x - points[i].x) * local,
				y: points[i].y + (points[i + 1].y - points[i].y) * local,
			};
		}
		remaining -= lengths[i];
	}
	return points[points.length - 1];
};

/** A comet's arc: a quadratic bezier bowed sideways off the straight line. */
export const arcPoint = (from: Point, to: Point, t: number, bow = 0.22): Point => {
	const midX = (from.x + to.x) / 2 - (to.y - from.y) * bow;
	const midY = (from.y + to.y) / 2 + (to.x - from.x) * bow;
	const inv = 1 - t;
	return {
		x: inv * inv * from.x + 2 * inv * t * midX + t * t * to.x,
		y: inv * inv * from.y + 2 * inv * t * midY + t * t * to.y,
	};
};

/** When (ms) a node the agent writes becomes real - its comet's landing. */
export const bornAtMs = (id: string): number | null => {
	const write = AGENT_WRITES.find((entry) => entry.id === id && entry.kind === 'born');
	return write ? write.at + COMET_MS : null;
};

export interface StageRect {
	x: number;
	y: number;
	width: number;
	height: number;
}

export interface AgentLayout {
	/** Where the graph is fitted while the chat is on stage (container px). */
	stage: StageRect;
	/** Where the agent tile docks (container px) - clear of graph, page and chat. */
	tile: Point;
	/** The edge of the mock homepage the tile's pulses fly into (container px). */
	page: Point;
}

/** Same breakpoint the chat overlay's container query uses. */
export const AGENT_WIDE_PX = 560;

/**
 * Wide: the mock homepage floats top-left with the chat under it, the graph
 * pulls back into the right half, and the tile docks between the two - reading
 * from one side, coding into the other. Narrow: the chat (carrying the page
 * inside it) takes the bottom half, the graph the top, the tile the graph's
 * free top-left corner.
 */
export const agentLayout = (width: number, height: number): AgentLayout =>
	width >= AGENT_WIDE_PX
		? {
				// The stage stops short of the right edge: touched nodes out there
				// still need room for their names.
				stage: { x: width * 0.5, y: 0, width: width * 0.5 - 48, height },
				tile: { x: width * 0.56, y: height * 0.1 },
				// A few px INSIDE the window (42% wide, capped at 300px, 8px down,
				// ~200px tall): the DOM window sits above the canvas, so the tether
				// runs under its edge and reads as plugged in, never as falling short.
				page: { x: Math.min(width * 0.42, 300) - 10, y: 8 + 96 },
			}
		: {
				stage: { x: 0, y: 0, width, height: height * 0.5 },
				tile: { x: width * 0.12, y: height * 0.08 },
				page: { x: width * 0.3, y: height * 0.56 },
			};

/**
 * Camera that fits `bounds` (graph units) inside `stage` (container px):
 * react-force-graph maps screen = (graph - center) * zoom + container / 2.
 */
export const cameraFor = (
	bounds: { minX: number; minY: number; maxX: number; maxY: number },
	stage: StageRect,
	container: { width: number; height: number },
	padding: number,
	maxZoom: number,
) => {
	const spanX = Math.max(1, bounds.maxX - bounds.minX);
	const spanY = Math.max(1, bounds.maxY - bounds.minY);
	const zoom = Math.min(
		maxZoom,
		Math.max(1, stage.width - padding * 2) / spanX,
		Math.max(1, stage.height - padding * 2) / spanY,
	);
	const stageCenterX = stage.x + stage.width / 2;
	const stageCenterY = stage.y + stage.height / 2;
	return {
		zoom,
		x: (bounds.minX + bounds.maxX) / 2 - (stageCenterX - container.width / 2) / zoom,
		y: (bounds.minY + bounds.maxY) / 2 - (stageCenterY - container.height / 2) / zoom,
	};
};
